import { Link } from 'react-router-dom';
import { LegalPageLayout, LegalSection } from '@/modules/marketing/components/legal-page-layout';
import { usePageSeo } from '@/modules/marketing/hooks/use-page-seo';

const LAST_UPDATED = 'September 7, 2026';

export function PrivacyPage() {
    usePageSeo({
        title: 'Privacy Policy',
        description:
            "Learn how AIPA collects, uses, and protects data across our AI Assistant, Pricing Intelligence, Sentiment Analysis, and CRM platform.",
        keywords: 'AIPA privacy policy, AIPA data protection, AI chatbot data privacy, GDPR, CCPA',
    });

    return (
        <LegalPageLayout title="Privacy Policy" lastUpdated={LAST_UPDATED}>
            <LegalSection title="1. Overview">
                <p>
                    This Privacy Policy explains how AIPA ("AIPA," "we," "us," or "our") collects, uses, discloses, and safeguards
                    information when you visit our website, use our AI Assistant chat widget, or use our Pricing Intelligence,
                    Sentiment Analysis, and CRM services (together, the "Service"). It applies to our own customers (workspace
                    administrators and their users) as well as end users who interact with an AI Assistant embedded on one of our
                    customers' websites.
                </p>
            </LegalSection>

            <LegalSection title="2. Information We Collect">
                <p>We collect information in the following ways:</p>
                <ul className="space-y-2">
                    <li>
                        <strong>Account &amp; workspace data</strong> — name, work email, phone number, company details, and role,
                        provided when you or your organization sign up for the Service.
                    </li>
                    <li>
                        <strong>Conversation &amp; lead data</strong> — messages exchanged with an AI Assistant, along with any
                        contact details a visitor chooses to share (name, email, phone), which becomes a lead in the relevant
                        workspace's CRM.
                    </li>
                    <li>
                        <strong>Contact &amp; demo requests</strong> — information you submit through our Contact or Book a Demo
                        forms, such as your name, email, phone, and message.
                    </li>
                    <li>
                        <strong>Usage data</strong> — pages visited, features used, and general device/browser information,
                        collected automatically to help us operate and improve the Service.
                    </li>
                    <li>
                        <strong>Cookies &amp; similar technologies</strong> — used for essential site functionality and, where you
                        consent via our cookie preferences banner, for analytics and similar purposes.
                    </li>
                </ul>
            </LegalSection>

            <LegalSection title="3. How We Use Information">
                <ul className="space-y-2">
                    <li>To provide, operate, and maintain the Service, including routing leads and conversations to the correct workspace;</li>
                    <li>To respond to contact and demo requests, and to send related account or transactional communications (for example, a demo-request confirmation email or, where configured, a WhatsApp message);</li>
                    <li>To generate pricing recommendations, sentiment insights, and other analytics from data you or your workspace provide;</li>
                    <li>To maintain the security of the Service and prevent fraud or abuse;</li>
                    <li>To comply with legal obligations.</li>
                </ul>
            </LegalSection>

            <LegalSection title="4. How We Share Information">
                <p>We do not sell personal information. We may share information with:</p>
                <ul className="space-y-2">
                    <li>
                        <strong>Service providers</strong> who help us operate the Service — for example, cloud hosting, email
                        delivery, and messaging providers (such as Twilio for SMS/WhatsApp) — bound by contractual confidentiality
                        and data-protection obligations;
                    </li>
                    <li>
                        <strong>AI providers</strong> that power the AI Assistant's responses, who process conversation content
                        strictly to generate a response and are not permitted to use it to train their own models except where you
                        or your workspace administrator has opted in;
                    </li>
                    <li>Your own workspace administrators and authorized users, who can see the leads and conversations generated within their workspace;</li>
                    <li>Authorities, where required to comply with the law or to protect the rights, property, or safety of AIPA, our users, or others.</li>
                </ul>
            </LegalSection>

            <LegalSection title="5. Cookies">
                <p>
                    We use essential cookies required for the Service to function, and — only with your consent — optional cookies
                    for analytics and similar purposes. You can review and update your cookie preferences at any time using the
                    cookie preferences control available on our website.
                </p>
            </LegalSection>

            <LegalSection title="6. Data Retention">
                <p>
                    We retain personal information for as long as needed to provide the Service and for legitimate business
                    purposes, such as maintaining lead and conversation history for your workspace, complying with legal
                    obligations, resolving disputes, and enforcing our agreements. When a workspace is closed, we retain or delete
                    its data in accordance with our data retention practices and any applicable contractual terms.
                </p>
            </LegalSection>

            <LegalSection title="7. Your Rights">
                <p>
                    Depending on where you live, you may have rights to access, correct, export, or delete your personal
                    information, or to object to or restrict certain processing. To exercise these rights, contact us at{' '}
                    <a href="mailto:hello@aipa.io">hello@aipa.io</a> or via our <Link to="/company/contact">Contact page</Link>. If
                    you are an end user who chatted with an AI Assistant on one of our customers' websites, we may need to direct
                    your request to that workspace, since they control the data collected through their assistant.
                </p>
            </LegalSection>

            <LegalSection title="8. Data Security">
                <p>
                    We use technical and organizational safeguards designed to protect personal information against unauthorized
                    access, alteration, disclosure, or destruction. No method of transmission or storage is completely secure, and
                    we cannot guarantee absolute security.
                </p>
            </LegalSection>

            <LegalSection title="9. International Transfers">
                <p>
                    Your information may be processed in countries other than your own. Where we transfer personal information
                    across borders, we use appropriate safeguards designed to ensure your information remains protected consistent
                    with this Privacy Policy and applicable law.
                </p>
            </LegalSection>

            <LegalSection title="10. Children's Privacy">
                <p>
                    The Service is not directed to children under 16, and we do not knowingly collect personal information from
                    children. If you believe a child has provided us with personal information, please contact us so we can delete
                    it.
                </p>
            </LegalSection>

            <LegalSection title="11. Changes to This Policy">
                <p>
                    We may update this Privacy Policy from time to time. If we make material changes, we will post the updated
                    policy on this page with a new "Last updated" date.
                </p>
            </LegalSection>

            <LegalSection title="12. Contact Us">
                <p>
                    Questions about this Privacy Policy can be sent to <a href="mailto:hello@aipa.io">hello@aipa.io</a>, or via our{' '}
                    <Link to="/company/contact">Contact page</Link>. See also our <Link to="/legal/terms">Terms &amp; Conditions</Link>.
                </p>
            </LegalSection>
        </LegalPageLayout>
    );
}
